import UserService from '@/services/user';
import { 
	AUTHENTICATE, 
	AUTHENTICATION_FAILED, 
	UPDATE_USER_INFO, 
	LOGOUT, 
	SET_USER_LOCATION,
	SHOW_LOGIN_MODAL,
	SHOW_SIGNUP_MODAL,
	HIDE_LOGIN_MODAL,
	HIDE_SIGNUP_MODAL,
	SHOW_RESET_MODAL,
	HIDE_RESET_MODAL
} from '../mutation-types';

const state = {
	location: JSON.parse(localStorage.getItem('location')) || {},
	isLoggedIn: localStorage.getItem('user') ? true : false,
	user: JSON.parse(localStorage.getItem('user')) || {},
	showLogin: false,
	showSignup: false,
	showReset: false,
};

const getters = {
	/**
	 * Check if the user has been authenticated.
	 * 
	 * @param  {Object} state
	 * @return {Boolean}      
	 */
	isLoggedIn(state) {
		return state.isLoggedIn;
	},

	/**
	 * Get the currently logged in user.
	 * 
	 * @param  {Object} state
	 * @return {Object}      
	 */
	getUser(state) {
		return state.user;
	},

	/**
	 * Check if the user has set his or her location
	 * 
	 * @param  {Object} state
	 * @return {Boolean}      
	 */
	isLocationSet(state) {
		return typeof state.location !== 'undefined' && Object.keys(state.location).length > 0 ? true : false;
	},

	/**
	 * Gets the current location of the user
	 * 	
	 * @param  {Object} state
	 * @return {Object}      
	 */
	getLocation(state) {
		return state.location;
	},

	/**
	 * Checks if we should show login modal
	 * 
	 * @param  {Object} state
	 * @return {Boolean}      
	 */
	shouldShowLogin(state) {
		return state.showLogin;
	},

	/**
	 * Checks if we should show signup modal
	 * 
	 * @param  {Object} state
	 * @return {Boolean}      
	 */
	shouldShowSignup(state) {
		return state.showSignup;
	},

	/**
	 * Checks if we should show password reset modal
	 *
	 * @param Object state 
	 * @return Boolean 
	 */
	shouldShowReset(state) {
		return state.showReset;
	}
};

const mutations = {
	[AUTHENTICATE] (state) {
		state.isLoggedIn = true;
	},

	[AUTHENTICATION_FAILED] (state) {
		state.isLoggedIn = false;
	},

	[UPDATE_USER_INFO] (state, user) {
		if (user) {
			state.user = user;
			localStorage.setItem('user', JSON.stringify(user));
		} else {
			state.user = {};
			localStorage.removeItem('user');
		}
	},

	[SET_USER_LOCATION] (state, location) {
		state.location = location;
		localStorage.setItem('location', JSON.stringify(location));
	},

	[LOGOUT] (state) {
		state.user = {};
		state.isLoggedIn = false;
	},

	[SHOW_LOGIN_MODAL] (state) {
		state.showLogin = true;
	},

	[HIDE_LOGIN_MODAL] (state) {
		state.showLogin = false;
	},

	[SHOW_SIGNUP_MODAL] (state) {
		state.showSignup = true;
	},

	[HIDE_SIGNUP_MODAL] (state) {
		state.showSignup = false;
	},

	[SHOW_RESET_MODAL] (state) {
		state.showReset = true;
	},

	[HIDE_RESET_MODAL] (state) {
		state.showReset = false;
	}
};

const actions = {
	/**
	 * Log in the specified user. Authenticate the user
	 * when the credentials are through or return errors.
	 * 
	 * @param  {Function} options.commit 
	 * @param  {Object} user           
	 * @return {Promise}                
	 */
	authenticate({ commit }, user) {
		return UserService.authenticate(user)
			.then((response) => {
				commit(AUTHENTICATE);
				commit(UPDATE_USER_INFO, response);

				return Promise.resolve(response);
			}, (error) => {
				commit(AUTHENTICATION_FAILED);
				commit(UPDATE_USER_INFO);

				return Promise.reject(error);
			});
	},

	/**
	 * Sign up new user with the data provided.
	 * After a successful operation, authenticate the user.
	 * 
	 * @param  {Function} options.commit 
	 * @param  {Object} user           
	 * @return {Promise}                
	 */
	signup({ commit }, user) {
		return UserService.store(user)
			.then((response) => {
				commit(AUTHENTICATE);
				commit(UPDATE_USER_INFO, response);

				return Promise.resolve(response);
			}, (error) => {
				commit(AUTHENTICATION_FAILED);
				commit(UPDATE_USER_INFO);

				return Promise.reject(error);
			});
	},

	/**
	 * Logout the authenticated user.
	 * 
	 * @param  {LOGOUT} options.commit
	 * @return null                
	 */
	logout({ commit }) {
		commit(LOGOUT);
		commit(UPDATE_USER_INFO);
	},

	/**
	 * Get the data of the authenticated user. Normally used
	 * for the my account component of the application.
	 * 
	 * @param  {Function} options.commit 
	 * @param  {Object} options.getters
	 * @return {Promise}               
	 */
	getUserInfo({ commit, getters }) {
		let user_id = getters.getUser.id;
		return UserService.getUserInfo(user_id)
			.then((response) => {				
				commit(UPDATE_USER_INFO, response);

				return Promise.resolve(response);
			})
			.catch((error) => {
				return Promise.reject(error);
			});
	},

	/**
	 * Update the authenticated users data.
	 * 	
	 * @param  {Function} options.commit 
	 * @param  {Object} options.getters
	 * @param  {Object} user            
	 * @return {Promise}                 
	 */
	updateUserInfo({ commit, getters }, user) {
		let user_id = getters.getUser.id;

		return UserService.updateUserInfo(user_id, user)
			.then((response) => {
				commit(UPDATE_USER_INFO, response);

				return Promise.resolve(response);
			})
			.catch((error) => {
				return Promise.reject(error);
			});
	},

	/**
	 * Update the current user's payload.
	 * 	
	 * @param  {Function} options.commit
	 * @param  {Object} payload      
	 * @return {Object}               
	 */
	updateLocation({commit}, payload) {
		if (payload.save === true) {
			let data = {
				town_id: payload.location.town_id
			};

			return UserService.updateUserTown(data)
				.then((response) => {
					commit(SET_USER_LOCATION, payload.location);

					return Promise.resolve(response);
				})
				.catch((error) => {
					return Promise.reject(error);
				});		
		} else {
			commit(SET_USER_LOCATION, payload.location);
			return Promise.resolve(payload.location);
		}
	},

	/**
	 * Sets the value of the showLogin state property
	 * 	
	 * @param {Function}  options.commit
	 * @param {Boolean} isShow         
	 * @return null
	 */
	setShowLogin({ commit }, isShow) {
		if (isShow === true) {
			commit(SHOW_LOGIN_MODAL);
		} else {
			commit(HIDE_LOGIN_MODAL);
		}
	},

	/**
	 * Sets the value of the showSignup state property
	 * 	
	 * @param {Function}  options.commit
	 * @param {Boolean} isShow         
	 * @return null
	 */
	setShowSignup({ commit }, isShow) {
		if (isShow === true) {
			commit(SHOW_SIGNUP_MODAL);
		} else {
			commit(HIDE_SIGNUP_MODAL);
		}
	},

	/**
	 * Sets the value of the showReset state property
	 * 	
	 * @param {Function}  options.commit
	 * @param {Boolean} isShow         
	 * @return null
	 */
	setShowReset({ commit }, isShow) {
		if (isShow === true) {
			commit(SHOW_RESET_MODAL);
		} else {
			commit(HIDE_RESET_MODAL);
		}
	},

	/**
	 * Get the confirmation token from api.
	 * 
	 * @param  {Function} options.commit
	 * @param  {String} mode           
	 * @return {Promise}                
	 */
	getConfirmationToken({ commit }, mode) {
		return UserService.getConfirmationToken(mode);
	},

	/**
	 * Confirm the confirmation token from api.
	 * 
	 * @param  {Function} options.commit
	 * @param  {Object} data           
	 * @return {Promise}                
	 */
	confirmConfirmationToken({ commit }, data) {
		return UserService.confirmConfirmationToken(data.rel, data.token)
			.then((response) => {
				commit(UPDATE_USER_INFO, response);

				return Promise.resolve(response);
			})
			.catch((error) => {
				return Promise.reject(error);
			});
	},

    /**
     * Send Request for new password reset token
     * 
     * @param  {[type]} options.commit 
     * @param  {[type]} data           
     * @return {[type]}                
     */
	requestPasswordReset({ commit }, data) {
		return UserService.requestPasswordReset(data)
		    .then((response) => Promise.resolve(response))
			.catch((error) => Promise.reject(error));
	},

    /**
     * Reset user password
     * 
     * @param  {Function} options.commit 
     * @param  {Object} data           
     * @return {Promise}              
     */
	resetPassword({ commit }, data) {
		return UserService.resetPassword(data)
		    .then((response) => Promise.resolve(response))
			.catch((error) => Promise.reject(error));
	},

	/**
	 * Send message from contact form
	 * 
	 * @param  {Object} options.commit
	 * @param  {Object} data          
	 * @return {Promise}               
	 */
	sendMessage({ commit }, data) {
		return UserService.sendMessage(data)
		    .then((response) => Promise.resolve(response))
			.catch((error) => Promise.reject(error));
	}
};

const namespaced = true;

export default {
	namespaced,
	state,
	actions,
	getters,
	mutations
}


// WEBPACK FOOTER //
// ./src/store/modules/user.js